// ============================================================================
// PERMISSIONS SYSTEM
// ============================================================================
// This file defines the permission structure for the three-level RBAC system:
// 1. Platform Level (admin, user, prospective_owner)
// 2. Nightclub Level (owner, manager, promoter, pr, accounting, security)
// 3. Event Level (organizer, ticket_manager, promoter, security, coordinator)
//
// IMPORTANT: Platform role permissions are NOT defined in this file.
// They are created and managed exclusively via database migrations.
// See: prisma/migrations/*
//
// Only NIGHTCLUB role permissions are defined here and used at runtime when
// creating new nightclubs to establish default roles.
// See: app/models/nightclub/create-nightclub.ts
//
// Permission Format: {action}:{access}:{entity}
// - action: create, read, update, delete
// - access: 'own' (user's assigned entities) or 'any' (all entities, admin only)
// - entity: The resource being accessed (nightclub, event, ticket, etc.)
//
// For complete RBAC documentation, see: docs/documentation/rbac-system.md
// ============================================================================

export const actions = [`create`, `read`, `update`, `delete`] as const
// 'own' means the user is the creator of the entity or is assigned to it.
// 'any' means the user can access all entities of that type (for admins only).
export const accesses = [`own`, `any`] as const
export const entities = [
	`user`, // Can update user profile (all users have it)
	`platform_invitation`, // Can send platform invitations
	`platform_role`, // Can manage platform users
	`platform_analytics`, // Can see platform analytics
	`nightclub`, // Can CRUD nightclubs
	`nightclub_financials`, // Can see nightclub financials
	`nightclub_analytics`, // Can see nightclub analytics
	`nightclub_role`, // Can manage nightclub roles
	`nightclub_invitation`, // Can send nightclub invitations
	`nightclub_role_assignment`, // Can assign nightclub roles to users
	`event`, // Can CRUD events
	`event_financials`, // Can see event financials
	`event_role`, // Can manage event roles
	`event_invitation`, // Can send event invitations
	`ticket`, // Can validate tickets
	`ticket_type`, // Can manage ticket types (e.g., VIP, General Admission)
	`purchase_session`, // Can see events purchase sessions (event sales analytics)
	`affiliate_link`, // Can create and read affiliate links
	`stripe_connect`, // Can manage Stripe Connect account
] as const

export type Action = (typeof actions)[number]
export type Access = (typeof accesses)[number]
export type Entity = (typeof entities)[number]

export type Permission = { action: Action; access: Access; entity: Entity }

export type PermissionString = `${Action}:${Access}:${Entity}`

export function parsePermissionString(perm: PermissionString): Permission {
	const [action, access, entity] = perm.split(`:`) as [Action, Access, Entity]
	return { action, access, entity }
}

export const platformRoles = {
	user: {
		name: `user`,
		description: `Default user role with basic permissions`,
	},
	admin: {
		name: `admin`,
		description: `Platform administrator with full permissions`,
	},
	prospective_owner: {
		name: `prospective_owner`,
		description: `User who can create nightclubs`,
	},
} as const

export const nightclubRoles = {
	owner: {
		name: `owner`,
		description: `Nightclub owner with full nightclub management permissions`,
	},
	manager: {
		name: `manager`,
		description: `Nightclub manager with event and ticket management permissions`,
	},
	promoter: {
		name: `promoter`,
		description: `Nightclub promoter with event creation permissions`,
	},
	pr: {
		name: `pr`,
		description: `Nightclub PR specialist with read-only access`,
	},
	accounting: {
		name: `accounting`,
		description: `Nightclub accountant with ticket read access`,
	},
	security: {
		name: `security`,
		description: `Nightclub security with event read access`,
	},
}

// System nightclub roles that cannot be deleted or renamed
export const SYSTEM_NIGHTCLUB_ROLES = [`owner`, `manager`, `promoter`] as const
export type SystemNightclubRole = (typeof SYSTEM_NIGHTCLUB_ROLES)[number]

export const isSystemNightclubRole = (
	roleName: string,
): roleName is SystemNightclubRole =>
	SYSTEM_NIGHTCLUB_ROLES.includes(roleName as SystemNightclubRole)

// Nightclub role hierarchy (lower numbers = higher privilege)
export const NIGHTCLUB_ROLE_HIERARCHY = {
	owner: 1,
	manager: 2,
	promoter: 3,
	pr: 4,
	accounting: 4,
	security: 4,
} as const

export type NightclubRoleHierarchy = keyof typeof NIGHTCLUB_ROLE_HIERARCHY

// Check if a user with inviterRole can invite someone to targetRole
export function canInviteRole(
	inviterRole: string,
	targetRole: string,
): boolean {
	const inviterLevel =
		NIGHTCLUB_ROLE_HIERARCHY[inviterRole as NightclubRoleHierarchy]
	const targetLevel =
		NIGHTCLUB_ROLE_HIERARCHY[targetRole as NightclubRoleHierarchy]

	// If either role is not in the hierarchy, deny by default
	if (inviterLevel === undefined || targetLevel === undefined) {
		return false
	}

	// Can invite roles at same level or lower privilege (higher number)
	return inviterLevel <= targetLevel
}

// ============================================================================
// NIGHTCLUB ROLE PERMISSIONS
// ============================================================================
// These permission arrays are used when creating a new nightclub to set up
// the default nightclub roles (owner, manager, promoter, pr, accounting, security).
// See: app/models/nightclub/create-nightclub.ts
//
// Platform roles (admin, user, prospective_owner) are NOT defined here.
// They are created and managed via database migrations only.
// ============================================================================

export const ownerPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `update`, access: `own`, entity: `nightclub` },
	{ action: `delete`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `nightclub_financials` },
	{ action: `read`, access: `own`, entity: `nightclub_analytics` },
	{ action: `create`, access: `own`, entity: `nightclub_role` },
	{ action: `read`, access: `own`, entity: `nightclub_role` },
	{ action: `update`, access: `own`, entity: `nightclub_role` },
	{ action: `delete`, access: `own`, entity: `nightclub_role` },
	{ action: `create`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `update`, access: `own`, entity: `event` },
	{ action: `delete`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `create`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` },
	{ action: `delete`, access: `own`, entity: `ticket` },
	{ action: `create`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `update`, access: `own`, entity: `ticket_type` },
	{ action: `delete`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
	{ action: `create`, access: `own`, entity: `nightclub_invitation` },
	{ action: `read`, access: `own`, entity: `nightclub_invitation` },
	{ action: `delete`, access: `own`, entity: `nightclub_invitation` },
	{ action: `create`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `read`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `update`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `delete`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `create`, access: `own`, entity: `affiliate_link` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `affiliate_link` },
	{ action: `delete`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `stripe_connect` },
]

export const managerPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `update`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `nightclub_financials` },
	{ action: `read`, access: `own`, entity: `nightclub_analytics` },
	{ action: `create`, access: `own`, entity: `nightclub_role` },
	{ action: `read`, access: `own`, entity: `nightclub_role` },
	{ action: `update`, access: `own`, entity: `nightclub_role` },
	{ action: `delete`, access: `own`, entity: `nightclub_role` },
	{ action: `create`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `update`, access: `own`, entity: `event` },
	{ action: `delete`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `create`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` },
	{ action: `delete`, access: `own`, entity: `ticket` },
	{ action: `create`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `update`, access: `own`, entity: `ticket_type` },
	{ action: `delete`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
	{ action: `create`, access: `own`, entity: `nightclub_invitation` },
	{ action: `read`, access: `own`, entity: `nightclub_invitation` },
	{ action: `delete`, access: `own`, entity: `nightclub_invitation` },
	{ action: `create`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `read`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `update`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `delete`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `create`, access: `own`, entity: `affiliate_link` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `affiliate_link` },
	{ action: `delete`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `stripe_connect` },
]

export const promoterPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `nightclub_analytics` },
	{ action: `create`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `update`, access: `own`, entity: `event` },
	{ action: `delete`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `create`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` },
	{ action: `delete`, access: `own`, entity: `ticket` },
	{ action: `create`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `update`, access: `own`, entity: `ticket_type` },
	{ action: `delete`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
	{ action: `create`, access: `own`, entity: `nightclub_invitation` },
	{ action: `read`, access: `own`, entity: `nightclub_invitation` },
	{ action: `delete`, access: `own`, entity: `nightclub_invitation` },
	{ action: `create`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `read`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `update`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `delete`, access: `own`, entity: `nightclub_role_assignment` },
	{ action: `create`, access: `own`, entity: `affiliate_link` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `affiliate_link` },
	{ action: `delete`, access: `own`, entity: `affiliate_link` },
]

// PR permissions - read-only access for public relations activities
export const prPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
]

// Accounting permissions - financial data access
export const accountingPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `nightclub_financials` },
	{ action: `read`, access: `own`, entity: `nightclub_analytics` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
]

// Security permissions - event and venue access
export const securityPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `nightclub` },
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` }, // For ticket validation
]

// ============================================================================
// EVENT ROLE PERMISSIONS
// ============================================================================
// These permission arrays define the default permissions for event roles.
// Unlike nightclub roles, event roles are typically created on-demand when
// assigning users to specific events, rather than being created automatically.
//
// Event roles are event-scoped - they only apply to the specific event they're
// assigned to, unlike nightclub roles which apply to all events at a nightclub.
// ============================================================================

// Event role templates
export const eventRoles = {
	organizer: {
		name: `organizer`,
		description: `Event organizer with full event management permissions`,
	},
	ticket_manager: {
		name: `ticket_manager`,
		description: `Event ticket manager with ticket sales and management permissions`,
	},
	promoter: {
		name: `promoter`,
		description: `Event promoter with limited event access`,
	},
	security: {
		name: `security`,
		description: `Event security with validation permissions`,
	},
	coordinator: {
		name: `coordinator`,
		description: `Event coordinator with operational permissions`,
	},
}

// Event role permissions
export const eventOrganizerPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `update`, access: `own`, entity: `event` },
	{ action: `delete`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `create`, access: `own`, entity: `event_role` },
	{ action: `read`, access: `own`, entity: `event_role` },
	{ action: `update`, access: `own`, entity: `event_role` },
	{ action: `delete`, access: `own`, entity: `event_role` },
	{ action: `create`, access: `own`, entity: `event_invitation` },
	{ action: `read`, access: `own`, entity: `event_invitation` },
	{ action: `delete`, access: `own`, entity: `event_invitation` },
	{ action: `create`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `update`, access: `own`, entity: `ticket_type` },
	{ action: `delete`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
	{ action: `create`, access: `own`, entity: `affiliate_link` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `affiliate_link` },
	{ action: `delete`, access: `own`, entity: `affiliate_link` },
]

export const eventTicketManagerPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `create`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
	{ action: `update`, access: `own`, entity: `ticket_type` },
	{ action: `delete`, access: `own`, entity: `ticket_type` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `purchase_session` },
]

export const eventPromoterPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `event_financials` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `create`, access: `own`, entity: `affiliate_link` },
	{ action: `read`, access: `own`, entity: `affiliate_link` },
	{ action: `update`, access: `own`, entity: `affiliate_link` },
	{ action: `delete`, access: `own`, entity: `affiliate_link` },
]

export const eventSecurityPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `update`, access: `own`, entity: `ticket` }, // For validation
]

export const eventCoordinatorPermissions: Permission[] = [
	{ action: `read`, access: `own`, entity: `event` },
	{ action: `update`, access: `own`, entity: `event` },
	{ action: `read`, access: `own`, entity: `ticket` },
	{ action: `read`, access: `own`, entity: `ticket_type` },
]

// Map event role names to their permission arrays
export const eventRolePermissionMap: Record<string, Permission[]> = {
	organizer: eventOrganizerPermissions,
	ticket_manager: eventTicketManagerPermissions,
	promoter: eventPromoterPermissions,
	security: eventSecurityPermissions,
	coordinator: eventCoordinatorPermissions,
}

// Event role hierarchy (lower numbers = higher privilege)
export const EVENT_ROLE_HIERARCHY = {
	organizer: 1,
	ticket_manager: 2,
	promoter: 3,
	security: 3,
	coordinator: 3,
} as const

export type EventRoleHierarchy = keyof typeof EVENT_ROLE_HIERARCHY

// Check if a user with inviterRole can invite someone to targetRole at event level
export function canInviteEventRole(
	inviterRole: string,
	targetRole: string,
): boolean {
	const inviterLevel =
		EVENT_ROLE_HIERARCHY[inviterRole as EventRoleHierarchy]
	const targetLevel =
		EVENT_ROLE_HIERARCHY[targetRole as EventRoleHierarchy]

	if (inviterLevel === undefined || targetLevel === undefined) {
		return false
	}

	return inviterLevel <= targetLevel
}
